import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalChangeCenter.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import { showSuccess, showError } from "../../services/toast.service";
import CenterSearchBar from "../CenterSearchBar";
import { updateDraftFolder } from "../../services/folder.service";

const ModalChangeCenter = ({ visible, onClose, onSave, folderId, currentCenterId }) => {
  const modalRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);

  // Gère le défilement du corps et réinitialise le formulaire
  useEffect(() => {
    const targetElement = modalRef.current || document.querySelector("#modal-change-center");

    if (visible) {
      if (targetElement) {
        disableBodyScroll(targetElement);
      }
      setIsSubmitting(false);
      // Réinitialiser la sélection à chaque ouverture de la modale
      setSelectedCenter(null);
    } else {
      clearAllBodyScrollLocks();
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [visible]);

  // Gère la touche 'Échap' pour fermer la modale
  const escFunction = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction);
    return () => {
      document.removeEventListener("keydown", escFunction);
    };
  }, [escFunction]);

  // Gère la sélection d'un centre
  const handleSelectCenter = (center) => {
    setSelectedCenter(center);
  };

  // Gère l'association d'un centre au dossier
  const handleAssignCenter = async () => {
    if (!selectedCenter || !folderId) {
      showError("Veuillez sélectionner un centre de formation");
      return;
    }

    setIsSubmitting(true);
    try {
      // Mettre à jour le dossier avec le nouveau centre
      await updateDraftFolder(folderId, {
        center_id: selectedCenter.id
      });

      showSuccess("Centre de formation modifié avec succès");
      
      if (onSave) {
        onSave(selectedCenter);
      }
      
      onClose();
    } catch (error) {
      console.error("Erreur lors de la modification du centre de formation:", error);
      showError("Erreur lors de la modification du centre de formation");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ne rend le portail que si la modale est visible
  if (!visible) {
    return null;
  }

  return createPortal(
    <>
      <div id="modal-change-center" className={styles.modal} ref={modalRef}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose} aria-label="Fermer la modale">
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer}>
          <Card
            className={styles.card}
            title="Modifier le centre de formation"
            classTitle="title-blue"
          >
            <div className={styles.container}>
      
              <CenterSearchBar
                label="Rechercher un centre de formation :"
                onSelectCenter={handleSelectCenter}
                showDetails={true}
                initialCenterId={visible ? currentCenterId : null}
                key={visible ? 'open' : 'closed'} // Force re-render when modal visibility changes
              />

              <div className={styles.divider} />

              {/* Boutons d'action */}
              <div className={styles.btnWrap}>
                <button 
                  type="button" 
                  className="button-stroke" 
                  onClick={onClose} 
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={handleAssignCenter}
                  disabled={isSubmitting || !selectedCenter}
                >
                  {isSubmitting ? "Modification en cours..." : "Modifier le centre"}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ModalChangeCenter;